/**
 * Bbox tiling + listing dedupe helpers for the Route Planner.
 *
 * Sharetribe caps `sdk.listings.query` at 100 results per page. A 15 km
 * corridor along a 500 km route covers ~15,000 km² — in popular regions
 * we WILL exceed 100 listings. Strategy: split the corridor's bbox into
 * tiles, query each tile in parallel, then dedupe the union. Works for any
 * route length; falls back to a single tile when the corridor is tiny.
 */

// Default tile size. 1° at mid-European latitudes is ~75 km × 111 km. Small
// enough that most tiles resolve in a single 100-listing page, large enough
// that a transcontinental route doesn't fan out into hundreds of requests.
const DEFAULT_TILE_DEGREES = 1;

// Safety cap. If a naive 1° split would produce more tiles than this, we
// scale the tile size up so we never fire more than this many parallel SDK
// queries.
const DEFAULT_MAX_TILES = 64;

/**
 * Split a bbox into row-major sub-tiles. Each tile is a `[minLng, minLat,
 * maxLng, maxLat]` quadruple. If the naive split would exceed `maxTiles`,
 * tile size is scaled up so the total stays under the cap.
 *
 * @param {[number, number, number, number] | null | undefined} bbox
 * @param {{ maxTileDegrees?: number, maxTiles?: number }} [options]
 * @returns {Array<[number, number, number, number]>}
 */
export const splitBBoxIntoTiles = (
  bbox,
  { maxTileDegrees = DEFAULT_TILE_DEGREES, maxTiles = DEFAULT_MAX_TILES } = {},
) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) return [];
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const widthDeg = maxLng - minLng;
  const heightDeg = maxLat - minLat;
  if (!(widthDeg > 0) || !(heightDeg > 0)) return [];

  let tileSize = maxTileDegrees;
  let cols = Math.ceil(widthDeg / tileSize);
  let rows = Math.ceil(heightDeg / tileSize);
  // Transcontinental route → scale up tile size to stay under maxTiles.
  // sqrt keeps rows/cols roughly balanced.
  while (cols * rows > maxTiles) {
    tileSize *= Math.sqrt((cols * rows) / maxTiles);
    cols = Math.ceil(widthDeg / tileSize);
    rows = Math.ceil(heightDeg / tileSize);
  }

  const tiles = [];
  for (let row = 0; row < rows; row += 1) {
    const tileMinLat = minLat + row * tileSize;
    const tileMaxLat = Math.min(tileMinLat + tileSize, maxLat);
    for (let col = 0; col < cols; col += 1) {
      const tileMinLng = minLng + col * tileSize;
      const tileMaxLng = Math.min(tileMinLng + tileSize, maxLng);
      tiles.push([tileMinLng, tileMinLat, tileMaxLng, tileMaxLat]);
    }
  }
  return tiles;
};

/**
 * Flatten an array-of-listing-arrays into one deduplicated array, keyed by
 * `listing.id.uuid`. Stable: the first occurrence of each listing wins.
 *
 * @param {Array<Array<object>>} listingArrays
 * @returns {Array<object>}
 */
export const dedupeListings = (listingArrays) => {
  if (!Array.isArray(listingArrays)) return [];
  const seen = new Set();
  const merged = [];
  for (const arr of listingArrays) {
    if (!Array.isArray(arr)) continue;
    for (const listing of arr) {
      const uuid = listing?.id?.uuid;
      if (!uuid || seen.has(uuid)) continue;
      seen.add(uuid);
      merged.push(listing);
    }
  }
  return merged;
};

/**
 * Dedupe + round-robin interleave. For a downstream `slice(0, cap)`, this
 * shares the cap fairly across tiles: with tile sizes [100, 5, 5, 5, 5] and
 * cap 100, naive concatenation gives 100 from tile 0 and zero from the rest;
 * interleave gives 5 from each sparse tile + 75 from the dense one.
 *
 * Equivalent to `dedupeListings` when all tiles have the same length — the
 * RoutePlanner's route-sample fanout relies on this to avoid starving sparse
 * segments of a long route while still letting dense segments fill unused
 * budget.
 *
 * @param {Array<Array<object>>} listingArrays
 * @returns {Array<object>}
 */
export const interleaveAndDedupeListings = (listingArrays) => {
  if (!Array.isArray(listingArrays)) return [];
  const seen = new Set();
  const merged = [];
  let maxLen = 0;
  for (const arr of listingArrays) {
    if (Array.isArray(arr) && arr.length > maxLen) maxLen = arr.length;
  }
  for (let i = 0; i < maxLen; i += 1) {
    for (const arr of listingArrays) {
      if (!Array.isArray(arr) || i >= arr.length) continue;
      const listing = arr[i];
      const uuid = listing?.id?.uuid;
      if (!uuid || seen.has(uuid)) continue;
      seen.add(uuid);
      merged.push(listing);
    }
  }
  return merged;
};
